import { useState } from "react";
import { CalendarClock, XCircle } from "lucide-react";
import { toast } from "sonner";
import type { AppointmentStatus } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { usePermissao } from "@/lib/use-permissao";
import { useConfirm } from "@/lib/confirm";
import type { WeekAppointment } from "@/lib/dashboard-week";
import { AgendaAppointmentDialog } from "@/components/agenda/AgendaAppointmentDialog";
import { consultaParaEdicao } from "@/components/agenda/consulta-para-edicao";

/** Só consulta que ainda vai acontecer se remarca ou cancela — atendida, em atendimento, cancelada
 *  ou com falta já têm a ação delas no botão principal (ver acao-da-consulta.ts). */
const AINDA_VAI_ACONTECER: AppointmentStatus[] = ["SCHEDULED", "CONFIRMED", "WAITING", "LATE"];

/**
 * "Remarcar" (abre a consulta no diálogo da Agenda, onde se troca dia e horário) e "Cancelar
 * consulta" no resumo da consulta do Início e da ficha. Só com `agenda.manage` e só pra consulta da
 * própria organização — a de outra organização (conta Pessoal) é só-leitura.
 */
export function RemarcarOuCancelar({ appointment, onConcluido }: { appointment: WeekAppointment; onConcluido: () => void }) {
  const utils = trpc.useUtils();
  const confirm = useConfirm();
  const podeMexer = usePermissao("agenda.manage");
  const [editando, setEditando] = useState(false);
  const settingsQuery = trpc.settings.getAgenda.useQuery(undefined, { enabled: podeMexer });

  const cancelar = trpc.appointment.update.useMutation({
    onSuccess: () => {
      toast.success("Consulta cancelada.");
      utils.appointment.invalidate();
      utils.team.list.invalidate();
      onConcluido();
    },
    onError: (e) => toast.error(e.message || "Não foi possível cancelar a consulta."),
  });

  if (!podeMexer || appointment.ehDeOutraOrganizacao || !AINDA_VAI_ACONTECER.includes(appointment.status)) {
    return null;
  }

  const pedirCancelamento = async () => {
    const ok = await confirm({
      title: `Cancelar a consulta de ${appointment.patient?.name ?? appointment.patientName}?`,
      description: "Ela continua no histórico como cancelada e o horário fica livre na agenda.",
      confirmText: "Cancelar consulta",
      cancelText: "Voltar",
      destructive: true,
    });
    if (ok) cancelar.mutate({ id: appointment.id, data: { status: "CANCELLED", cancelledBy: "professional" } });
  };

  return (
    <>
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => setEditando(true)}
          disabled={!settingsQuery.data}
          className="inline-flex h-9 items-center justify-center gap-1.5 rounded-md border border-zinc-200 px-3 text-sm font-semibold text-zinc-700 transition-colors hover:bg-zinc-50 disabled:opacity-60 dark:border-border dark:text-foreground dark:hover:bg-muted"
        >
          <CalendarClock className="h-4 w-4" />
          Remarcar
        </button>
        <button
          type="button"
          onClick={pedirCancelamento}
          disabled={cancelar.isPending}
          className="inline-flex h-9 items-center justify-center gap-1.5 rounded-md border border-red-200 px-3 text-sm font-semibold text-red-600 transition-colors hover:bg-red-50 disabled:opacity-60 dark:border-red-500/30 dark:text-red-400 dark:hover:bg-red-500/10"
        >
          <XCircle className="h-4 w-4" />
          {/* "Cancelar consulta" quebrava em duas linhas no celular; a confirmação diz o resto. */}
          Cancelar
        </button>
      </div>

      {editando && settingsQuery.data && (
        <AgendaAppointmentDialog
          open
          mode="edit"
          initial={consultaParaEdicao(appointment as Parameters<typeof consultaParaEdicao>[0])}
          settings={settingsQuery.data}
          onClose={() => setEditando(false)}
          onSaved={() => {
            setEditando(false);
            utils.appointment.invalidate();
            onConcluido();
          }}
        />
      )}
    </>
  );
}
